import * as THREE from 'three';
import { latLonToPoint3D } from './geodata';
import { randomBetween } from './util';
import type { CountryData } from './countries';

function addPolygonData(
  coords: GeoJSON.Position[],
  radius: number,
  colour: THREE.Color,
  combinedVertices: number[],
  combinedIndices: number[],
  colours: number[],
  vertexIndex: { value: number },
): void {
  const vertices: number[] = [];
  const indices: number[] = [];

  for (const coord of coords) {
    const [lng, lat] = coord;
    const point = latLonToPoint3D(lat, lng, radius);
    vertices.push(point.x, point.y, point.z);
    colours.push(colour.r, colour.g, colour.b);
    indices.push(vertexIndex.value++);
  }

  const finalIndices: number[] = [];
  for (let i = 0; i < indices.length - 1; i++) {
    finalIndices.push(indices[i], indices[i + 1]);
  }
  finalIndices.push(indices[indices.length - 1], indices[0]);

  combinedVertices.push(...vertices);
  combinedIndices.push(...finalIndices);
}

function getFullOutline(
  country: CountryData,
  radius: number,
  colour: THREE.Color,
  vertices: number[],
  indices: number[],
  colours: number[],
  vertexIndex: { value: number },
): void {
  if (country.geometry.type === 'Polygon') {
    addPolygonData(country.geometry.coordinates[0], radius, colour, vertices, indices, colours, vertexIndex);
  } else if (country.geometry.type === 'MultiPolygon') {
    for (const polygon of country.geometry.coordinates) {
      addPolygonData(polygon[0], radius, colour, vertices, indices, colours, vertexIndex);
    }
  }
}

export function buildBorderLines(
  countries: CountryData[],
  radius: number,
): { lines: THREE.LineSegments; countryColors: Map<string, THREE.Color> } {
  const vertices: number[] = [];
  const indices: number[] = [];
  const colours: number[] = [];
  const vertexIndex = { value: 0 };
  const countryColors = new Map<string, THREE.Color>();

  for (const country of countries) {
    const colour = new THREE.Color(randomBetween(0.1, 1.0), randomBetween(0.1, 1.0), randomBetween(0.1, 1.0));
    countryColors.set(country.id, colour);
    getFullOutline(country, radius, colour, vertices, indices, colours, vertexIndex);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colours, 3));
  geometry.setIndex(indices);

  const material = new THREE.LineBasicMaterial({ vertexColors: true, linewidth: 1 });
  return { lines: new THREE.LineSegments(geometry, material), countryColors };
}
